import * as Sentry from "@sentry/react";

const HEARTBEAT_MESSAGE = "kiosk-liveness-heartbeat";
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

type CaptureMessage = typeof Sentry.captureMessage;

type LivenessHeartbeatOptions = {
  intervalMs?: number;
  captureMessage?: CaptureMessage;
};

// @sentry/react doesn't expose withMonitor/cron check-ins (that's a
// backend-SDK feature) — a periodic info-level message is the supported
// browser-SDK equivalent. Alerting on a missed heartbeat is a Sentry
// alert-rule concern (e.g. "no kiosk-liveness-heartbeat in N minutes"),
// not something this module can configure.
export class LivenessHeartbeat {
  private isRunning = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly intervalMs: number;
  private readonly captureMessage: CaptureMessage;

  constructor(options: LivenessHeartbeatOptions = {}) {
    this.intervalMs = options.intervalMs ?? HEARTBEAT_INTERVAL_MS;
    this.captureMessage = options.captureMessage ?? Sentry.captureMessage;
  }

  start(): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.checkIn();
    this.intervalId = setInterval(() => this.checkIn(), this.intervalMs);
  }

  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private checkIn(): void {
    this.captureMessage(HEARTBEAT_MESSAGE, "info");
  }
}

export const livenessHeartbeat = new LivenessHeartbeat();
